import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import ToolTipIcon from '../../../components/ToolTipIcon/ToolTipIcon';
import {
  setCopyConfig,
  requestDiagnostics,
  requestScreenshot,
  upgradeDevice,
  clearData,
  select,
} from '../../../actions/deviceActions';
import { addNotification } from '../../../actions/notificationActions';
import { getPermissions } from '../../../utils/getPermissions';
import { checkPathAccess } from '../../../utils/checkPathAccess';
import getActiveCampaignId from '../../../components/CampaignSchedule/utils/getActiveCampaignId';
import { classNames } from '../../../utils/v4/utils';

function DeviceActions(props) {
  const {
    setShowUnregisterDialog,
    addNotification,
    setShowDeployDialog,
    onRequestDiagnostics,
    onRequestScreenshot,
    setShowRebootDialog,
    setShowRestartDialog,
    setShowClearDataDialog,
    setShowCopyConfigDialog,
    setShowUpgradeDialog,
    campaignScheduleEntries,
    onLeave,
    copyConfig,
    device,
    deviceLocation,
    disabled,
    id,
    isPasteConfigEnabled,
    overrideDownloadSchedule,
  } = props;
  let pasteConfigToolText = 'Paste Device Settings';

  if (copyConfig) {
    pasteConfigToolText = (
      <span>
        Paste Device Settings from
        <br />
        <code>{copyConfig.name}</code>
        <br />
        <b>{copyConfig.description}</b>
      </span>
    );
  }

  const setCopyConfig = () => {
    const { setCopyConfig } = props;

    setCopyConfig(device);
    const text = (
      <span>
        Device Settings copied from <code key={device.id}>{device.name}</code> {device.description}
      </span>
    );

    addNotification({ type: 'success', text });
  };

  const { auth } = props;
  const permissions = getPermissions(auth, 'devices');
  const isAccessAllowed = checkPathAccess(auth.user.user.userLocation, deviceLocation);
  let deployFn = setShowDeployDialog;
  let deployToolText = 'Deploy Content';

  if (!isAccessAllowed) {
    deployToolText =
      'Deploy Content. Deploy Content. Location is not accessible with current User Location.';

    deployFn = () => {};
  }

  return (
    <div
      data-test="device-actions"
      className="single-device grid grid-cols-[repeat(5,_max-content)] md:grid-cols-[repeat(7,_max-content)] xl:grid-cols-[repeat(14,_max-content)] gap-2 mt-8"
    >
      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText={deployToolText}>
          <button
            data-test="device-actions-deploy-content"
            type="button"
            onClick={async () => {
              deployFn(true);
            }}
            className={classNames(
              'duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg',
              isAccessAllowed ? '' : 'opacity-50',
            )}
          >
            <img
              src="/assets/images/icons/deploy_button.svg"
              alt="Deploy"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}

      {permissions.atLeastTierProOrSuperUser &&
        permissions.downloadSchedule &&
        permissions.deviceManager && (
          <ToolTipIcon toolText="Override Download Schedule">
            <button
              data-test="device-actions-override-download-schedule"
              type="button"
              onClick={overrideDownloadSchedule}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/schedule_icon.svg"
                alt="Deploy"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}

      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText={disabled ? 'Edit Device Settings' : 'Device Overview'}>
          <Link
            data-test="device-actions-edit-device-settings"
            type="button"
            onClick={onLeave}
            to={`/devices/${id}${disabled ? '?edit' : ''}`}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src={
                disabled
                  ? '/assets/images/icons/pencil_button.svg'
                  : '/assets/images/icons/eye_device.svg'
              }
              alt={disabled ? 'Edit' : 'Device Overview'}
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </Link>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && (
        <ToolTipIcon toolText="Request Diagnostics">
          <button
            data-test="device-actions-request-diagnostics"
            type="button"
            onClick={async () => {
              onRequestDiagnostics();
            }}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/diagnostics_button.svg"
              alt="Diagnostics"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && (
        <ToolTipIcon toolText="Request Screenshot">
          <button
            data-test="device-actions-request-screenshot"
            type="button"
            onClick={() => {
              onRequestScreenshot();
            }}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/camera_button.svg"
              alt="Capture"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText="Restart Player">
          <button
            data-test="device-actions-restart-player"
            type="button"
            onClick={() => setShowRestartDialog(true)}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/refresh_button.svg"
              alt="Restart Player"
              className="w-[30px] h-[30px]"
            />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText="Reboot Device">
          <button
            data-test="device-actions-reboot-device"
            type="button"
            onClick={() => setShowRebootDialog(true)}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/reboot_button.svg"
              alt="reboot"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText="Upgrade Device">
          <button
            data-test="device-actions-upgrade-device"
            type="button"
            onClick={async () => setShowUpgradeDialog(true)}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img src="/assets/images/icons/upgrade_button.png" alt="Upgrade" draggable={false} />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText="Clear Signage Data">
          <button
            data-test="device-actions-clear-signage-data"
            type="button"
            onClick={() => setShowClearDataDialog(true)}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/clear_button.svg"
              alt="Clear"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}

      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && permissions.admin && (
        <ToolTipIcon toolText="Unregister Device">
          <button
            data-test="device-actions-unregister-device"
            type="button"
            onClick={setShowUnregisterDialog}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/delete_button.svg"
              alt="Delete"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}

      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText="Copy Device Settings">
          <button
            data-test="device-actions-copy-device-settings"
            type="button"
            onClick={async () => {
              setCopyConfig();
            }}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/copy_button.svg"
              alt="Copy"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}

      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText={pasteConfigToolText}>
          <button
            data-test="device-actions-paste-device-settings"
            type="button"
            onClick={() => setShowCopyConfigDialog(true)}
            className={classNames(
              'duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg ',
              isPasteConfigEnabled() ? '' : 'opacity-50',
            )}
          >
            <img
              src="/assets/images/icons/paste_button.svg"
              alt="Paste"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && (
        <ToolTipIcon toolText="Device Preview">
          <Link
            to={`/player/?device=${id}&campaign=${getActiveCampaignId(campaignScheduleEntries)}`}
            type="button"
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            target="_blank"
          >
            <img
              src="/assets/images/icons/black_play_button.svg"
              alt="Preview Device content"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </Link>
        </ToolTipIcon>
      )}
    </div>
  );
}

DeviceActions.propTypes = {
  setCopyConfig: PropTypes.func.isRequired,
  copyConfig: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
  setShowUnregisterDialog: PropTypes.func.isRequired,
  setShowDeployDialog: PropTypes.func.isRequired,
  onRequestDiagnostics: PropTypes.func.isRequired,
  onRequestScreenshot: PropTypes.func.isRequired,
  setShowRebootDialog: PropTypes.func.isRequired,
  setShowRestartDialog: PropTypes.func.isRequired,
  setShowClearDataDialog: PropTypes.func.isRequired,
  setShowCopyConfigDialog: PropTypes.func.isRequired,
  setShowUpgradeDialog: PropTypes.func.isRequired,
  isPasteConfigEnabled: PropTypes.func.isRequired,
  overrideDownloadSchedule: PropTypes.func,
  onLeave: PropTypes.func,
  campaignScheduleEntries: PropTypes.array,
  auth: PropTypes.object.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  device: PropTypes.object.isRequired,
  deviceLocation: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  disabled: PropTypes.bool,
};
const mapDispatchToProps = {
  setCopyConfig,
  requestDiagnostics,
  requestScreenshot,
  upgradeDevice,
  clearData,
  select,
  addNotification,
};
const mapStateToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceActions);
